import { memo } from 'react';
import { useFormContext, Controller } from 'FrontRoyalReactHookForm';
import { type AutocompleteValue, Autocomplete as MuiAutocomplete, TextField as MuiTextField } from '@mui/material';
import { type AutocompleteProps } from './FrontRoyalMaterialUiForm.types';

function AutocompleteComponent<T, M extends boolean, D extends boolean = false, F extends boolean = false>({
    name,
    label,
    variant,
    ...rest
}: AutocompleteProps<T, M, D, F>) {
    const { control } = useFormContext();
    const defaultValue = rest.multiple ? [] : null;

    return (
        <Controller
            control={control}
            name={name}
            render={({ field }) => (
                <MuiAutocomplete
                    {...field}
                    value={(field.value || defaultValue) as AutocompleteValue<T, M, D, F>}
                    // MUI calls onChange as (event, value). Spreading `field` alone hands it
                    // react-hook-form's handler, which then stores the event, so the form ends up
                    // with the wrong value. Every caller used to pass its own onChange to work
                    // around this; `rest` still overrides this default for those that do.
                    onChange={(_event, value) => field.onChange(value)}
                    {...rest}
                    renderInput={props => <MuiTextField {...props} label={label} variant={variant} />}
                />
            )}
        />
    );
}

export const Autocomplete = memo(AutocompleteComponent) as typeof AutocompleteComponent;

export default Autocomplete;
